import { Injectable, Inject, DOCUMENT } from '@angular/core';

import { EMPTY } from 'rxjs';

import { SsoOptionsService } from './sso-options.service';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * sessionStorage key backing up the requested deep link. The ADFS `state` query param is the
 * primary carrier of the return path, but some ADFS configs don't echo it back verbatim, so this
 * survives the same full-page redirect round trip (same tab, same origin) as a fallback.
 */
export const SSO_RETURN_URL_STORAGE_KEY = 'sso_return_url';

@Injectable()
export class LoginRedirectService {
  // Multiple guards/interceptors can call redirectWithState() for the same navigation attempt
  // (e.g. authGuard then activeGuard's hard-reset path) - without this, a later call wins the
  // document.location race and can send the browser to '/login' with the wrong (or no) state,
  // even though localStorage itself already holds the correct deep link. First call wins.
  private isRedirecting = false;

  constructor(
    private ssoOptions: SsoOptionsService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  public redirectWithState(explicitState?: string) {
    if (this.isRedirecting) {
      return EMPTY;
    }
    this.isRedirecting = true;
    const { redirectUrl } = this.ssoOptions.options;
    this.storeReturnUrl(explicitState);
    const nextUrl = `${redirectUrl}${this.appendStateParam(explicitState)}`;
    this.logger.warn(nextUrl);
    this.document.location = nextUrl;
    return EMPTY; // returning EMPTY to prevent multiple redirections.
  }

  private storeReturnUrl(explicitState?: string): void {
    const deepLinkPath = this.normalizeDeepLink(explicitState) ?? this.currentDeepLinkPath;
    if (deepLinkPath) {
      // localStorage (not sessionStorage) - some environments don't reliably carry
      // sessionStorage through the backend -> Microsoft -> backend redirect chain.
      localStorage.setItem(SSO_RETURN_URL_STORAGE_KEY, deepLinkPath);
    } else {
      // No deep link for this login attempt (e.g. session expired on the home page) - clear
      // any stale value from a previous attempt so this login doesn't get redirected to it.
      localStorage.removeItem(SSO_RETURN_URL_STORAGE_KEY);
    }
  }

  // '/' (or '') is the app root, not a real deep link to return to - treat it as absent
  // whether it came in explicitly (e.g. a guard's resolved state.url) or implicitly.
  private normalizeDeepLink(value?: string): string | null {
    return value && value !== '/' ? value : null;
  }

  private get currentDeepLinkPath(): string | null {
    const { pathname, search } = this.document.location;
    if (['', '/'].some(p => p === pathname) || pathname?.includes('adfs-callback')) {
      return null;
    }
    return `${pathname}${search}`;
  }

  private appendStateParam(explicitState?: string): string {
    const deepLinkPath = this.normalizeDeepLink(explicitState) ?? this.currentDeepLinkPath;
    return deepLinkPath ? `?state=${encodeURIComponent(deepLinkPath)}` : '';
  }
}
